import type {
  BookingMode,
  City,
  Interest,
  NotificationItem,
  Plan,
  PlanInput,
  PlanResult,
  PlanStage,
  Reservation,
  VenueDetails,
  VenueSummary,
  WeatherContext,
} from "../domain/models";

export const cities: City[] = [
  {
    id: "beirut",
    name: "Beirut",
    region: "Beirut",
    latitude: 33.8938,
    longitude: 35.5018,
  },
  {
    id: "jounieh",
    name: "Jounieh",
    region: "Keserwan",
    latitude: 33.9808,
    longitude: 35.6178,
  },
  {
    id: "byblos",
    name: "Byblos",
    region: "Jbeil",
    latitude: 34.123,
    longitude: 35.6519,
  },
  {
    id: "batroun",
    name: "Batroun",
    region: "North Lebanon",
    latitude: 34.2554,
    longitude: 35.6581,
  },
  {
    id: "zahle",
    name: "Zahlé",
    region: "Beqaa",
    latitude: 33.8463,
    longitude: 35.902,
  },
];

const photos = {
  museum:
    "https://images.unsplash.com/photo-1564399579883-451a5d44ec08?auto=format&fit=crop&w=1000&q=80",
  cafe: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1000&q=80",
  park: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1000&q=80",
  family:
    "https://images.unsplash.com/photo-1504151932400-72d4384f04b3?auto=format&fit=crop&w=1000&q=80",
  dining:
    "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1000&q=80",
  nightlife:
    "https://images.unsplash.com/photo-1566737236500-c8ac43014a8e?auto=format&fit=crop&w=1000&q=80",
  coast:
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1000&q=80",
  wellness:
    "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=1000&q=80",
};

const venue = (
  id: string,
  venueName: string,
  branchName: string,
  area: string,
  category: string,
  interests: Interest[],
  priceBand: 1 | 2 | 3 | 4,
  image: string,
  bookingModes: BookingMode[] = ["walk_in"],
): VenueSummary => ({
  id,
  venueName,
  branchName,
  address: `${area}, Lebanon`,
  area,
  latitude: 33.89 + Number(id.slice(1)) * 0.002,
  longitude: 35.5 + Number(id.slice(1)) * 0.003,
  priceBand,
  activityCategories: [category],
  bookingModes,
  hasRequestToBook: bookingModes.includes("request_to_book"),
  interests,
  rating: 4.5,
  image,
  isOpen: true,
});
export const venues: VenueSummary[] = [
  venue(
    "v1",
    "Sursock Museum",
    "Achrafieh",
    "Achrafieh",
    "culture_sightseeing",
    ["culture", "chill_relaxed"],
    1,
    photos.museum,
    ["walk_in"],
  ),
  venue(
    "v2",
    "Café Younes",
    "Hamra",
    "Hamra",
    "coffee_dessert",
    ["food_focused", "chill_relaxed"],
    2,
    photos.cafe,
    ["no_booking_required"],
  ),
  venue(
    "v3",
    "Horsh Beirut",
    "Badaro",
    "Badaro",
    "outdoor_nature",
    ["outdoor", "family_friendly"],
    1,
    photos.park,
    ["no_booking_required"],
  ),
  venue(
    "v4",
    "MIM Mineral Museum",
    "USJ",
    "Beirut",
    "family_activities",
    ["culture", "family_friendly"],
    2,
    photos.family,
    ["walk_in"],
  ),
  venue(
    "v5",
    "Liza",
    "Achrafieh",
    "Achrafieh",
    "food_dining",
    ["romantic", "upscale_luxurious", "food_focused"],
    4,
    photos.dining,
    ["request_to_book"],
  ),
  venue(
    "v6",
    "Iris",
    "Waterfront",
    "Beirut Waterfront",
    "nightlife",
    ["party_dancing", "upscale_luxurious"],
    4,
    photos.nightlife,
    ["request_to_book"],
  ),
  venue(
    "v7",
    "Byblos Old Port",
    "Harbour",
    "Byblos",
    "culture_sightseeing",
    ["culture", "outdoor", "romantic"],
    1,
    photos.coast,
    ["no_booking_required"],
  ),
  venue(
    "v8",
    "The Spa Beirut",
    "Downtown",
    "Beirut",
    "wellness",
    ["wellness", "chill_relaxed"],
    3,
    photos.wellness,
    ["request_to_book"],
  ),
];
export const details: VenueDetails[] = venues.map((item) => ({
  ...item,
  description: `A distinctive ${item.activityCategories[0]?.replaceAll("_", " ")} experience in ${item.area}.`,
  minimumAge: item.activityCategories.includes("nightlife") ? 21 : 0,
  depositMinor: item.hasRequestToBook ? 2500 : 0,
  currency: "USD",
  phone: "+961 1 555 010",
  openingHours: [
    "Mon–Thu 9:00 AM–10:00 PM",
    "Fri–Sat 9:00 AM–1:00 AM",
    "Sun 10:00 AM–9:00 PM",
  ],
  amenities: ["Accessible entrance", "Family-friendly facilities"],
  packages: ["General admission", "Small-group option"],
  cancellationPolicy: item.hasRequestToBook
    ? "Full deposit refund when cancelled at least 24 hours before the booking."
    : "No Spot booking is required.",
}));

const availableWeather: WeatherContext = {
  availability: "available",
  condition: "Clear, 24°C",
  temperatureCelsius: 24,
  precipitationProbability: 0.1,
  sourceTimestamp: "2026-09-21T06:00:00Z",
  requiresRefresh: false,
};
const futureWeather: WeatherContext = {
  availability: "unavailable",
  condition: null,
  temperatureCelsius: null,
  precipitationProbability: null,
  sourceTimestamp: null,
  requiresRefresh: true,
};
const route = (minutes: number, distance = 1800) => ({
  distanceMetres: distance,
  durationSeconds: minutes * 60,
  provider: "development-routing",
  calculatedAt: "2026-09-21T06:00:00Z",
  trafficMode: "estimated",
});
const stage = (
  id: string,
  index: number,
  venueItem: VenueSummary,
  offeringName: string,
  category: string,
  start: string,
  end: string,
  cost: number,
  bookingMode: BookingMode,
  travelMinutes: number,
  weatherDecision: PlanStage["weatherDecision"] = "suitable",
  minimumAge = 0,
): PlanStage => ({
  id,
  stage: index,
  category,
  activityOfferingId: `offering-${id}`,
  branchId: venueItem.id,
  offeringName,
  venueName: venueItem.venueName,
  branchName: venueItem.branchName,
  address: venueItem.address,
  startsAt: start,
  endsAt: end,
  estimatedPerPersonCostMinor: cost,
  bookingMode,
  reservationRequired: bookingMode === "request_to_book",
  depositRequired: bookingMode === "request_to_book" && cost >= 2500,
  route: route(travelMinutes),
  weatherDecision,
  reason: `Fits your ${venueItem.interests[0]?.replaceAll("_", " ")} preference and available time.`,
  isRedacted: false,
  featured: false,
  featuredLabel: null,
  image: venueItem.image,
  minimumAge,
  rowVersion: 1,
});
const plan = (
  id: string,
  alternative: number,
  reason: string,
  stages: PlanStage[],
  weather: WeatherContext,
  featured = false,
): Plan => ({
  id,
  alternative,
  startsAt: stages[0]!.startsAt,
  endsAt: stages.at(-1)!.endsAt,
  estimatedPerPersonCostMinor: stages.reduce(
    (sum, item) => sum + item.estimatedPerPersonCostMinor,
    0,
  ),
  estimatedPartyCostMinor:
    stages.reduce((sum, item) => sum + item.estimatedPerPersonCostMinor, 0) * 2,
  totalRouteDistanceMetres: stages.reduce(
    (sum, item) => sum + (item.route?.distanceMetres ?? 0),
    0,
  ),
  totalRouteDurationSeconds: stages.reduce(
    (sum, item) => sum + (item.route?.durationSeconds ?? 0),
    0,
  ),
  weather,
  reason,
  featured,
  featuredLabel: featured ? "Sponsored suggestion" : null,
  stages,
});

const basePlans: Plan[] = [
  plan(
    "plan-daytime",
    1,
    "Two thoughtful hours in Achrafieh",
    [
      stage(
        "day-1",
        1,
        venues[0]!,
        "Museum highlights",
        "culture_sightseeing",
        "2026-09-23T07:00:00Z",
        "2026-09-23T08:15:00Z",
        1000,
        "walk_in",
        12,
      ),
      stage(
        "day-2",
        2,
        venues[1]!,
        "Coffee and something sweet",
        "coffee_dessert",
        "2026-09-23T08:28:00Z",
        "2026-09-23T09:00:00Z",
        1200,
        "no_booking_required",
        13,
      ),
    ],
    availableWeather,
  ),
  plan(
    "plan-family",
    2,
    "A relaxed half-day for the family",
    [
      stage(
        "family-1",
        1,
        venues[3]!,
        "Mineral discovery visit",
        "family_activities",
        "2026-09-23T06:30:00Z",
        "2026-09-23T08:00:00Z",
        800,
        "walk_in",
        18,
      ),
      stage(
        "family-2",
        2,
        venues[4]!,
        "Lebanese family lunch",
        "food_dining",
        "2026-09-23T08:15:00Z",
        "2026-09-23T09:45:00Z",
        3500,
        "request_to_book",
        15,
      ),
      stage(
        "family-3",
        3,
        venues[2]!,
        "Open-air play and walk",
        "outdoor_nature",
        "2026-09-23T10:00:00Z",
        "2026-09-23T11:00:00Z",
        0,
        "no_booking_required",
        15,
      ),
      stage(
        "family-4",
        4,
        venues[1]!,
        "Dessert stop",
        "coffee_dessert",
        "2026-09-23T11:12:00Z",
        "2026-09-23T11:30:00Z",
        900,
        "walk_in",
        12,
      ),
    ],
    availableWeather,
  ),
  plan(
    "plan-nightlife",
    3,
    "Dinner, music and a late finish",
    [
      stage(
        "night-1",
        1,
        venues[4]!,
        "Dinner experience",
        "food_dining",
        "2026-09-23T17:00:00Z",
        "2026-09-23T19:00:00Z",
        5000,
        "request_to_book",
        20,
      ),
      stage(
        "night-2",
        2,
        venues[5]!,
        "Rooftop music",
        "nightlife",
        "2026-09-23T19:18:00Z",
        "2026-09-23T21:30:00Z",
        4500,
        "request_to_book",
        18,
        "suitable",
        21,
      ),
      stage(
        "night-3",
        3,
        venues[1]!,
        "Late coffee",
        "coffee_dessert",
        "2026-09-23T21:42:00Z",
        "2026-09-23T22:15:00Z",
        1000,
        "walk_in",
        12,
      ),
    ],
    availableWeather,
  ),
  plan(
    "plan-mixed",
    4,
    "Wellness, food and a waterfront walk",
    [
      stage(
        "mixed-1",
        1,
        venues[7]!,
        "Restorative treatment",
        "wellness",
        "2026-09-23T11:00:00Z",
        "2026-09-23T12:00:00Z",
        4000,
        "request_to_book",
        14,
      ),
      stage(
        "mixed-2",
        2,
        venues[4]!,
        "Seasonal lunch",
        "food_dining",
        "2026-09-23T12:14:00Z",
        "2026-09-23T13:45:00Z",
        4000,
        "request_to_book",
        14,
      ),
      stage(
        "mixed-3",
        3,
        venues[2]!,
        "Sunset walk",
        "outdoor_nature",
        "2026-09-23T14:05:00Z",
        "2026-09-23T15:00:00Z",
        0,
        "no_booking_required",
        20,
        "caution",
      ),
    ],
    availableWeather,
    true,
  ),
  plan(
    "plan-coast",
    5,
    "Culture and the coast in Byblos",
    [
      stage(
        "coast-1",
        1,
        venues[6]!,
        "Old port and souk walk",
        "culture_sightseeing",
        "2026-10-25T07:00:00Z",
        "2026-10-25T09:00:00Z",
        0,
        "no_booking_required",
        22,
        "unknown",
      ),
      stage(
        "coast-2",
        2,
        venues[4]!,
        "Lunch by the coast",
        "food_dining",
        "2026-10-25T09:15:00Z",
        "2026-10-25T10:45:00Z",
        3500,
        "request_to_book",
        15,
        "unknown",
      ),
      stage(
        "coast-3",
        3,
        venues[1]!,
        "Coffee break",
        "coffee_dessert",
        "2026-10-25T11:00:00Z",
        "2026-10-25T11:40:00Z",
        1000,
        "walk_in",
        15,
        "unknown",
      ),
    ],
    futureWeather,
  ),
];

function redactStage(item: PlanStage): PlanStage {
  return {
    ...item,
    activityOfferingId: null,
    branchId: null,
    offeringName: null,
    venueName: null,
    branchName: null,
    address: null,
    estimatedPerPersonCostMinor: 0,
    bookingMode: "redacted",
    reservationRequired: false,
    depositRequired: false,
    route: null,
    weatherDecision: "redacted",
    reason: "",
    isRedacted: true,
    featured: false,
    featuredLabel: null,
    image: undefined,
    minimumAge: undefined,
  };
}
export function fixturePlans(locked = true, input?: PlanInput): PlanResult {
  const requestId =
    input?.generationMode === "surprise"
      ? "request-demo-spot-surprise"
      : "request-demo-spot-001";
  return {
    requestId,
    plans: basePlans.map((item) => ({
      ...item,
      stages: item.stages.map((stageItem) =>
        locked && stageItem.stage > 1 ? redactStage(stageItem) : stageItem,
      ),
    })),
  };
}

export const reservation: Reservation = {
  id: "res-001",
  status: "in_progress",
  requestedAt: new Date().toISOString(),
  walkInStages: [basePlans[3]!.stages[2]!],
  stages: [
    {
      venueRequestId: "vr-1",
      venueName: "The Spa Beirut",
      branchName: "Downtown",
      startsAt: "2026-09-23T11:00:00Z",
      bookingMode: "request_to_book",
      status: "confirmed",
      paymentStatus: "paid",
      depositMinor: 2500,
      currency: "USD",
    },
    {
      venueRequestId: "vr-2",
      venueName: "Liza",
      branchName: "Achrafieh",
      startsAt: "2026-09-23T12:14:00Z",
      bookingMode: "request_to_book",
      status: "replacement_required",
      paymentStatus: "not_required",
      depositMinor: 2500,
      currency: "USD",
      replacementWorkflowId: "rw-1",
    },
  ],
};
export const notifications: NotificationItem[] = [
  {
    id: "n1",
    kind: "replacement_required",
    title: "Choose a replacement",
    body: "One booking request was declined. Your confirmed booking remains in place.",
    createdAt: new Date().toISOString(),
    read: false,
    deepLink: "/reservations/res-001",
  },
  {
    id: "n2",
    kind: "weather_refresh",
    title: "Weather update available",
    body: "Refresh your upcoming coastal plan for the latest forecast context.",
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    read: true,
    deepLink: "/plans/plan-coast",
  },
];
